import React, { useState, useEffect } from "react";
import { Offcanvas, Badge, Button, Spinner, Alert } from "react-bootstrap";
import { Icon } from "../../../utils/iconHelper";
import { StudyResource } from "../../../_services/StudyResourceService";
import StudyResourceService from "../../../_services/StudyResourceService";
import ApiService from "../../../_services/ApiService";
// [discussion-board type=20] role list reused to label comment-role ids
import DiscussionBoardService, { RoleBean } from "../../../_services/DiscussionBoardService";
import { STUDY_RESOURCE_TYPE } from "../../../_constants/studyResourceType";
import Config from "../../../_config/config";

function isS3Url(url: string): boolean {
  return url.includes(".s3.") && url.includes("amazonaws.com");
}

// Native learner-rendered types: their URL is an ilearn public asset that also
// needs ?resourceId + token appended by ilearn's resource route — opening the
// raw path from the iteach origin 404s. Open these through ilearn instead.
const LEARNER_RENDERED_TYPES: number[] = [
  STUDY_RESOURCE_TYPE.INTERACTIVE_PATHWAY,
  STUDY_RESOURCE_TYPE.SPIN_WHEEL,
];

function learnerResourceUrl(resource: StudyResource): string {
  const base = Config.ilearnUrl.replace(/\/+$/, "");
  return `${base}/studyResource/${resource.typeId}/${resource.subTypeId ?? 0}/details/${resource.id}`;
}

interface StudyResourceViewDrawerProps {
  show: boolean;
  onHide: () => void;
  resource: StudyResource | null;
  onEdit?: (resource: StudyResource) => void;
  onIssue?: (resource: StudyResource) => void;
}

const StudyResourceViewDrawer: React.FC<StudyResourceViewDrawerProps> = ({
  show,
  onHide,
  resource,
  onEdit,
  onIssue,
}) => {
  const [signing, setSigning] = useState(false);
  const [ragifyingModuleId, setRagifyingModuleId] = useState<number | null>(null);
  const [ragifyFeedback, setRagifyFeedback] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);

  // [discussion-board type=20] role-id → name lookup for the "Accept Comments from Role(s)" field
  const [boardRoles, setBoardRoles] = useState<RoleBean[]>([]);

  useEffect(() => {
    if (!show || resource?.typeId !== STUDY_RESOURCE_TYPE.DISCUSSION_BOARD || boardRoles.length > 0) return;
    DiscussionBoardService.listRoles()
      .then((res) => setBoardRoles(res.data?.data || []))
      .catch(() => setBoardRoles([]));
  }, [show, resource?.typeId, boardRoles.length]);

  // [discussion-board type=20] map a comma-separated id string to role names (falls back to ids)
  const resolveRoleNames = (ids: string | null | undefined): string => {
    if (!ids) return "Anyone";
    const idList = ids.split(",").map((s) => s.trim()).filter(Boolean);
    if (idList.length === 0) return "Anyone";
    return idList
      .map((id) => boardRoles.find((r) => String(r.roleId) === id)?.roleName ?? `#${id}`)
      .join(", ");
  };

  async function handleRagify(moduleId: number, moduleName: string) {
    setRagifyingModuleId(moduleId);
    setRagifyFeedback(null);
    try {
      await ApiService.embedLessonPlan(moduleId);
      setRagifyFeedback({ type: 'success', message: `RAGify started for "${moduleName}"` });
    } catch (err: any) {
      setRagifyFeedback({ type: 'danger', message: `Failed: ${err.message || 'Unknown error'}` });
    } finally {
      setRagifyingModuleId(null);
    }
  }

  function openResource(res: StudyResource) {
    if (LEARNER_RENDERED_TYPES.includes(res.typeId)) {
      window.open(learnerResourceUrl(res), "_blank", "noopener,noreferrer");
      return;
    }
    openUrl(res.url!);
  }

  async function openUrl(url: string) {
    if (!url) return;
    if (!isS3Url(url)) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    const newWindow = window.open("", "_blank");
    setSigning(true);
    try {
      const signedUrl = await StudyResourceService.getSignedUrl(url);
      if (newWindow) newWindow.location.href = signedUrl;
    } catch {
      if (newWindow) newWindow.location.href = url;
    } finally {
      setSigning(false);
    }
  }

  const getTypeBadgeColor = (typeId: number) => {
    switch (typeId) {
      case 1: return "primary";
      case 5: return "info";
      case 6: return "success";
      case 31: return "warning";
      case 32: return "danger";
      case 36: return "dark";
      default: return "secondary";
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      style={{ width: "420px" }}
      className="study-resource-view-drawer"
    >
      <Offcanvas.Header closeButton className="border-bottom">
        <Offcanvas.Title>Resource Details</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="p-0 d-flex flex-column">
        {resource ? (
          <>
            <div className="p-3 flex-grow-1 overflow-auto">
              {/* Name */}
              <div className="mb-3">
                <label className="text-muted small d-block mb-1">Name</label>
                <div className="fw-semibold" style={{ fontSize: "16px" }}>{resource.name}</div>
              </div>

              {/* Type */}
              <div className="mb-3">
                <label className="text-muted small d-block mb-1">Type</label>
                <Badge bg={getTypeBadgeColor(resource.typeId)}>
                  {resource.typeName || StudyResourceService.getResourceTypeName(resource.typeId)}
                </Badge>
              </div>

              {/* [discussion-board type=20] board details from study_discussion_board (linked by Id) */}
              {resource.typeId === STUDY_RESOURCE_TYPE.DISCUSSION_BOARD && (
                <>
                  {resource.description && (
                    <div className="mb-3">
                      <label className="text-muted small d-block mb-1">Description</label>
                      <div style={{ fontSize: "13px", whiteSpace: "pre-wrap" }}>{resource.description}</div>
                    </div>
                  )}

                  <div className="mb-3">
                    <label className="text-muted small d-block mb-1">Status</label>
                    <span
                      style={{
                        display: "inline-block",
                        padding: "3px 10px",
                        borderRadius: 12,
                        fontSize: 12,
                        fontWeight: 500,
                        backgroundColor: resource.active ? "#e8f5e9" : "#fdecea",
                        color: resource.active ? "#2e7d32" : "#c62828",
                      }}
                    >
                      {resource.active ? "Active" : "Inactive"}
                    </span>
                  </div>

                  <div className="mb-3">
                    <label className="text-muted small d-block mb-1">Allow students to post new topics</label>
                    <span style={{ fontSize: "13px" }}>{resource.acceptStudentPosts ? "Yes" : "No"}</span>
                  </div>

                  <div className="mb-3">
                    <label className="text-muted small d-block mb-1">Accept Comments from Role(s)</label>
                    <span style={{ fontSize: "13px" }}>{resolveRoleNames(resource.acceptCommentFromRoleIds)}</span>
                  </div>

                  {resource.icon && (
                    <div className="mb-3">
                      <label className="text-muted small d-block mb-1">Icon URL</label>
                      <a
                        href={resource.icon}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="d-flex align-items-center gap-1 text-break"
                        style={{ fontSize: "13px" }}
                      >
                        {resource.icon}
                        <Icon.ExternalLink size={12} />
                      </a>
                    </div>
                  )}

                  {resource.sendToEmails && (
                    <div className="mb-3">
                      <label className="text-muted small d-block mb-1">Notify emails</label>
                      <div style={{ fontSize: "13px", whiteSpace: "pre-wrap" }}>{resource.sendToEmails}</div>
                    </div>
                  )}
                </>
              )}

              {/* URL — hidden for Discussion Board: its URL == the icon, already shown above */}
              {resource.url && resource.typeId !== STUDY_RESOURCE_TYPE.DISCUSSION_BOARD && (
                <div className="mb-3">
                  <label className="text-muted small d-block mb-1">URL</label>
                  <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); openResource(resource); }}
                    className="d-flex align-items-center gap-1 text-break"
                    style={{ fontSize: "13px" }}
                  >
                    {resource.url}
                    <Icon.ExternalLink size={12} />
                  </a>
                </div>
              )}

              {/* Content (for Articles) */}
              {resource.content && (
                <div className="mb-3">
                  <label className="text-muted small d-block mb-1">Content</label>
                  <div
                    className="p-2 bg-light rounded"
                    style={{
                      maxHeight: "200px",
                      overflowY: "auto",
                      fontSize: "13px",
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {resource.content}
                  </div>
                </div>
              )}

              {/* Rubric */}
              <div className="mb-3">
                <label className="text-muted small d-block mb-1">Rubric</label>
                {resource.rubricName ? (
                  <Badge bg="warning" text="dark">{resource.rubricName}</Badge>
                ) : (
                  <span className="text-muted" style={{ fontSize: "13px" }}>Not assigned</span>
                )}
              </div>

              {/* AI Config */}
              <div className="mb-3">
                <label className="text-muted small d-block mb-1">AI Configuration</label>
                {resource.hasAiConfig ? (
                  <Badge bg="success">Enabled</Badge>
                ) : (
                  <span className="text-muted" style={{ fontSize: "13px" }}>Not configured</span>
                )}
              </div>

              {/* RAG Status */}
              <div className="mb-3">
                <label className="text-muted small d-block mb-1">RAG Status</label>
                {resource.isRagified ? (
                  <span style={{ color: 'var(--brand-accent)', fontWeight: 500, fontSize: '13px' }}>✦ RAG-ified</span>
                ) : (
                  <span className="text-muted" style={{ fontSize: "13px" }}>Not indexed</span>
                )}
              </div>

              {/* Learning Modules */}
              <div className="mb-3">
                <label className="text-muted small d-block mb-1">Learning Modules</label>
                {ragifyFeedback && (
                  <Alert variant={ragifyFeedback.type} dismissible onClose={() => setRagifyFeedback(null)} className="py-1 px-2 mb-2" style={{ fontSize: '12px' }}>
                    {ragifyFeedback.message}
                  </Alert>
                )}
                {resource.learningModules && resource.learningModules.length > 0 ? (
                  <div>
                    {resource.learningModules.map((module) => (
                      <div key={module.id} className="d-flex align-items-center gap-2 mb-1">
                        <a
                          href={`/lm-builder/${module.id}?mode=view`}
                          style={{ fontSize: '13px', textDecoration: 'none' }}
                        >
                          {module.name}
                        </a>
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() => handleRagify(module.id, module.name)}
                          disabled={ragifyingModuleId === module.id}
                          style={{ color: 'var(--brand-accent)', padding: 0, fontSize: '12px', textDecoration: 'none' }}
                          title={`RAGify ${module.name}`}
                        >
                          {ragifyingModuleId === module.id ? <Spinner role="status" animation="border" size="sm" /> : '✦ RAGify'}
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted" style={{ fontSize: "13px" }}>Not in any modules</span>
                )}
              </div>

              <hr />

              {/* Metadata */}
              <div className="row">
                <div className="col-6 mb-2">
                  <label className="text-muted small d-block mb-1">ID</label>
                  <span style={{ fontSize: "13px" }}>{resource.id}</span>
                </div>
                <div className="col-6 mb-2">
                  <label className="text-muted small d-block mb-1">Instructor ID</label>
                  <span style={{ fontSize: "13px" }}>{resource.instructorId || "N/A"}</span>
                </div>
                <div className="col-6 mb-2">
                  <label className="text-muted small d-block mb-1">Created</label>
                  <span style={{ fontSize: "13px" }}>{formatDate(resource.creationDate)}</span>
                </div>
                <div className="col-6 mb-2">
                  <label className="text-muted small d-block mb-1">Modified</label>
                  <span style={{ fontSize: "13px" }}>{formatDate(resource.lastModifiedDate)}</span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-top px-3 py-2 bg-light mt-auto">
              <div className="d-flex gap-2">
                {LEARNER_RENDERED_TYPES.includes(resource.typeId) ? (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => openResource(resource)}
                    className="d-flex align-items-center gap-1"
                    style={{ fontSize: '12px', padding: '4px 10px' }}
                  >
                    <Icon.ExternalLink size={12} />
                    Open in iLearn
                  </Button>
                ) : resource.url && resource.typeId !== STUDY_RESOURCE_TYPE.DISCUSSION_BOARD ? (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => openUrl(resource.url!)}
                    disabled={signing}
                    className="d-flex align-items-center gap-1"
                    style={{ fontSize: '12px', padding: '4px 10px' }}
                  >
                    {signing ? <Spinner role="status" animation="border" size="sm" /> : <Icon.ExternalLink size={12} />}
                    Open Resource
                  </Button>
                ) : null}
                {onEdit && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      onHide();
                      onEdit(resource);
                    }}
                    className="d-flex align-items-center gap-1"
                    style={{ fontSize: '12px', padding: '4px 10px' }}
                  >
                    <Icon.Edit2 size={12} />
                    Edit
                  </Button>
                )}
                {onIssue && (
                  <Button
                    variant="success"
                    size="sm"
                    onClick={() => {
                      onHide();
                      onIssue(resource);
                    }}
                    className="d-flex align-items-center gap-1"
                    style={{ fontSize: '12px', padding: '4px 10px' }}
                  >
                    <Icon.Send size={12} />
                    Issue to Batches
                  </Button>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="p-3 text-center text-muted">
            No resource selected
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default StudyResourceViewDrawer;
